import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { getProfileAssetUrl } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { MessageCircle, Bell, User, Settings, LogOut, Search, Calendar, Menu, X } from 'lucide-react';
import ChatDropdownContent from './ChatDropdown';

const ACADEMIC_YEAR_KEY = 'selected_academic_year_id';

export default function TopNav({ isSidebarOpen, setSidebarOpen }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const mobileSearchRef = useRef(null);
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  const schoolId = user?.school_id;

  useEffect(() => {
    setSearchQuery(searchParams.get('q') || '');
  }, [searchParams]);

  useEffect(() => {
    if (showMobileSearch) {
      setTimeout(() => mobileSearchRef.current?.focus(), 0);
    }
  }, [showMobileSearch]);

  useEffect(() => {
    if (!schoolId) {
      setAcademicYears([]);
      setSelectedYearId('');
      return;
    }
    const storageKey = `${ACADEMIC_YEAR_KEY}_${schoolId}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) setSelectedYearId(saved);
    api.get('/settings/academic-years', { params: { school_id: schoolId } })
      .then((res) => {
        const years = res.data.data || [];
        setAcademicYears(years);
        if (!saved && years.length > 0) {
          const current = years.find((y) => y.is_current);
          const id = (current || years[0]).id.toString();
          setSelectedYearId(id);
          localStorage.setItem(storageKey, id);
        }
      })
      .catch(() => setAcademicYears([]));
  }, [schoolId]);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications', { params: { limit: 8 } });
      const data = res.data?.data || {};
      setNotifications(data.items || []);
      setUnreadCount(Number(data.unread_count || 0));
    } catch (e) {
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const fetchChatUnread = async () => {
    try {
      const res = await api.get('/chat/unread-count');
      const count = Number(res.data?.data?.unread_contacts_count || 0);
      setChatUnreadCount(count);
    } catch (e) {
      setChatUnreadCount(0);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
      fetchChatUnread();
      const id = setInterval(() => {
        if (document.visibilityState === 'visible') {
          fetchChatUnread();
        }
      }, 30000);
      return () => clearInterval(id);
    }
  }, [user?.id]);

  useEffect(() => {
    if (notifOpen) {
      fetchNotifications();
    }
  }, [notifOpen]);

  useEffect(() => {
    if (chatOpen) {
      fetchChatUnread();
    }
  }, [chatOpen]);

  const handleAcademicYearChange = (value) => {
    setSelectedYearId(value);
    if (schoolId) {
      localStorage.setItem(`${ACADEMIC_YEAR_KEY}_${schoolId}`, value);
      window.dispatchEvent(new CustomEvent('academicYearChanged', { detail: { schoolId, academicYearId: value } }));
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery?.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
    else navigate('/dashboard');
  };

  const handleProfile = () => navigate('/profile');
  const handleSettings = () => navigate('/settings');
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationClick = async (notif) => {
    try {
      if (!notif?.is_read) {
        await api.patch(`/notifications/${notif.id}/read`);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, is_read: 1, read_at: new Date().toISOString() } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } finally {
      if (notif?.link) {
        navigate(notif.link);
      }
    }
  };

  const handleMarkAllRead = async () => {
    await api.post('/notifications/read-all');
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1, read_at: n.read_at || new Date().toISOString() })));
    setUnreadCount(0);
  };

  const userAvatarUrl = user?.avatar
    ? (user.avatar.startsWith('http') ? user.avatar : getProfileAssetUrl(user.avatar))
    : null;
  const initials = [user?.first_name?.[0], user?.last_name?.[0]].filter(Boolean).join('').toUpperCase() || 'U';

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-4 border-b border-slate-800 bg-slate-900/95 backdrop-blur px-4 lg:px-6">
      <div className="flex items-center gap-2 min-w-0">
        {/* Mobile sidebar toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden text-slate-200 hover:text-white hover:bg-slate-800"
          onClick={() => setSidebarOpen(!isSidebarOpen)}
          aria-label="Toggle sidebar"
        >
          {isSidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>

        {/* Global search (desktop) */}
        <form onSubmit={handleSearchSubmit} className="hidden md:block w-full max-w-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              type="search"
              placeholder="Search students, teachers, classes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 bg-slate-800/80 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-slate-600"
            />
          </div>
        </form>

        {/* Global search (mobile) */}
        {showMobileSearch && (
          <form onSubmit={handleSearchSubmit} className="md:hidden flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                ref={mobileSearchRef}
                type="search"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 h-9 bg-slate-800/80 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-slate-600"
              />
              <button
                type="button"
                onClick={() => setShowMobileSearch(false)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                aria-label="Close search"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="ml-auto flex items-center gap-2">
        {/* Academic year selector */}
        {!showMobileSearch && schoolId && academicYears.length > 0 && (
          <Select value={selectedYearId || undefined} onValueChange={handleAcademicYearChange}>
            <SelectTrigger className="w-[140px] h-9 bg-slate-800 border-slate-700 text-slate-200">
              <Calendar className="h-4 w-4 mr-1.5 text-slate-500" />
              <SelectValue placeholder="Academic year" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {academicYears.map((y) => (
                <SelectItem key={y.id} value={y.id.toString()} className="text-slate-200 focus:bg-slate-700">
                  {y.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {!showMobileSearch && (
          <>
            {/* Search icon (mobile) */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-slate-400 hover:text-white hover:bg-slate-800 md:hidden"
              onClick={() => setShowMobileSearch(true)}
              title="Search"
            >
              <Search className="h-5 w-5" />
            </Button>

            {/* Chat */}
            <DropdownMenu onOpenChange={setChatOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-9 w-9 text-slate-400 hover:text-white hover:bg-slate-800"
                  title="Chat"
                >
                  <MessageCircle className="h-5 w-5" />
                  {chatUnreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                      {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-0 border-0 bg-transparent shadow-none">
                <ChatDropdownContent open={chatOpen} onUnreadChanged={fetchChatUnread} />
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Notifications */}
            <DropdownMenu onOpenChange={setNotifOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-slate-400 hover:text-white hover:bg-slate-800 relative"
                  title="Notifications"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 bg-slate-800 border-slate-700 text-slate-200">
                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700">
                  <span className="text-sm font-semibold text-white">Notifications</span>
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={handleMarkAllRead}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-3 py-6 text-sm text-slate-400 text-center">No notifications</div>
                  ) : (
                    notifications.map((notif) => (
                      <DropdownMenuItem
                        key={notif.id}
                        onClick={() => handleNotificationClick(notif)}
                        className={`cursor-pointer focus:bg-slate-700 ${
                          notif.is_read ? 'text-slate-400' : 'text-white'
                        }`}
                      >
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-medium">{notif.title}</span>
                          {notif.message && <span className="text-xs text-slate-400">{notif.message}</span>}
                          {notif.created_at && (
                            <span className="text-[10px] text-slate-500">
                              {new Date(notif.created_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

          </>
        )}

        {/* Profile dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full p-0">
              <Avatar className="h-9 w-9 border-2 border-slate-700">
                {userAvatarUrl ? (
                  <AvatarImage src={userAvatarUrl} alt={user?.first_name} />
                ) : null}
                <AvatarFallback className="bg-slate-700 text-white text-sm">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700 text-slate-200">
            <div className="px-2 py-2 border-b border-slate-700">
              <p className="text-sm font-medium text-white truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              <p className="text-xs text-slate-500">{user?.role}</p>
            </div>
            <DropdownMenuItem onClick={handleProfile} className="cursor-pointer focus:bg-slate-700 focus:text-white">
              <User className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleSettings} className="cursor-pointer focus:bg-slate-700 focus:text-white">
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-slate-700" />
            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer focus:bg-red-500/20 focus:text-red-400">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
